import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Discount tiers based on total quantity
const getDiscountTier = (totalQuantity) => {
  if (totalQuantity >= 25) return { percentage: 20, minItems: 25, nextTier: null };
  if (totalQuantity >= 15) return { percentage: 15, minItems: 15, nextTier: 25 };
  if (totalQuantity >= 10) return { percentage: 10, minItems: 10, nextTier: 15 };
  if (totalQuantity >= 5) return { percentage: 5, minItems: 5, nextTier: 10 };
  return { percentage: 0, minItems: 0, nextTier: 5 };
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [previousDiscountTier, setPreviousDiscountTier] = useState(0);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Check for discount tier changes
    const totalQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);
    const currentTier = getDiscountTier(totalQuantity);
    
    if (currentTier.percentage > previousDiscountTier) {
      // Unlocked new discount
      let message = `🎉 Congratulations! You've unlocked ${currentTier.percentage}% discount!`;
      if (currentTier.nextTier) {
        message += ` Add ${currentTier.nextTier - totalQuantity} more plates to unlock ${currentTier.nextTier >= 25 ? '20%' : currentTier.nextTier >= 15 ? '15%' : currentTier.nextTier >= 10 ? '10%' : '5%'} discount!`;
      } else {
        message += ` Maximum discount reached!`;
      }
      toast.success(message, { duration: 5000 });
    } else if (currentTier.percentage < previousDiscountTier && previousDiscountTier > 0) {
      // Lost discount tier
      toast.error(`Your ${previousDiscountTier}% discount has been removed. Add more items to unlock it again!`, { duration: 4000 });
    }
    
    setPreviousDiscountTier(currentTier.percentage);
  }, [cart]);

  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getDiscount = () => {
    const totalQuantity = getCartCount();
    const tier = getDiscountTier(totalQuantity);
    const subtotal = getCartTotal();
    return {
      percentage: tier.percentage,
      amount: (subtotal * tier.percentage) / 100,
      nextTier: tier.nextTier,
      itemsToNextTier: tier.nextTier ? tier.nextTier - totalQuantity : 0,
    };
  };

  const getDeliveryFee = () => {
    const totalQuantity = getCartCount();
    // Delivery fee of $9.99 for orders with less than 10 plates
    if (totalQuantity > 0 && totalQuantity < 10) {
      return 9.99;
    }
    return 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        getDiscount,
        getDeliveryFee,
        isCartOpen,
        setIsCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};